import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import type { IInstrumentTemplateSummary } from '@brushfire/types'
import metaDurationIcon from '../assets/icons/meta-duration.svg'
import metaQuestionsIcon from '../assets/icons/meta-questions.svg'
import { formatTemplateEstCompletionLabel } from '@/features/instruments/templates/lib/formatTemplateEstCompletion'
import { PlatformTemplateTileMenu } from '@/features/platform/components/PlatformTemplateTileMenu'
import { libraryCardHoverSx } from '@/shared/lib/libraryCardHover'
import { MetaUsageIcon } from './MetaUsageIcon'
import { TemplateIcon } from './TemplateIcon'

const CARD_BORDER = '#e2e8f0'
const CARD_BORDER_SELECTED = '#008cd0'
const TITLE = '#04183f'
const BODY = '#3f4850'
const META_QUESTIONS = '#008cd0'
const META_DURATION = '#0d9467'
const META_USAGE = '#92400e'

/** Hidden platform-admin card — reduced opacity + amber badge */
const HIDDEN_CARD_BG = 'rgba(255, 255, 255, 0.4)'
const HIDDEN_TITLE = 'rgba(4, 24, 63, 0.4)'
const HIDDEN_BODY = 'rgba(63, 72, 80, 0.4)'
const HIDDEN_META_QUESTIONS = 'rgba(0, 140, 208, 0.4)'
const HIDDEN_META_DURATION = 'rgba(7, 82, 57, 0.36)'
const HIDDEN_META_USAGE = 'rgba(146, 64, 14, 0.4)'
const HIDDEN_BADGE_BG = '#fef3c7'
const HIDDEN_BADGE_TEXT = '#b45309'

type Props = Readonly<{
  template: IInstrumentTemplateSummary
  selected: boolean
  interactive: boolean
  onOpen?: () => void
  tileVariant?: 'sensemaker' | 'platformAdmin'
  onRequestHide?: (template: IInstrumentTemplateSummary) => void
  onRequestUnhide?: (template: IInstrumentTemplateSummary) => void
}>

function formatPlatformUsageLabel(count: number): string {
  return `Used ${count} times`
}

export function TemplateTile({
  template,
  selected,
  interactive,
  onOpen,
  tileVariant = 'sensemaker',
  onRequestHide,
  onRequestUnhide,
}: Props) {
  const isPlatformAdmin = tileVariant === 'platformAdmin'
  const isHiddenPlatformCard = isPlatformAdmin && Boolean(template.hidden)
  const usageCount = template.publishedUsageCount ?? 0

  const tileSx: SxProps<Theme> = {
    position: 'relative',
    border: `1px solid ${selected ? CARD_BORDER_SELECTED : CARD_BORDER}`,
    borderRadius: '12px',
    bgcolor: isHiddenPlatformCard ? HIDDEN_CARD_BG : '#ffffff',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '24px',
    minHeight: '162px',
    p: '25px',
    textAlign: 'left',
    width: '100%',
    ...(interactive
      ? {
          appearance: 'none',
          cursor: 'pointer',
          ...libraryCardHoverSx,
          // Keep selected primary border on hover (don't fall back to slate).
          ...(selected
            ? {
                '&:hover': {
                  borderWidth: '1.8px',
                  borderStyle: 'solid',
                  borderColor: CARD_BORDER_SELECTED,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
                },
              }
            : {}),
        }
      : { cursor: 'default' }),
  }

  const content = (
    <>
      {isPlatformAdmin && onRequestHide && onRequestUnhide ? (
        <PlatformTemplateTileMenu
          template={template}
          onHide={onRequestHide}
          onUnhide={onRequestUnhide}
        />
      ) : null}
      <TemplateIcon iconKey={template.iconKey} muted={isHiddenPlatformCard} />
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, pr: isPlatformAdmin ? 3 : 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              color: isHiddenPlatformCard ? HIDDEN_TITLE : TITLE,
            }}
          >
            {template.name}
          </Typography>
          {isHiddenPlatformCard ? (
            <Box
              component="span"
              data-testid={`platform-template-hidden-badge-${template.slug}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: '8px',
                py: '2px',
                borderRadius: '999px',
                bgcolor: HIDDEN_BADGE_BG,
                color: HIDDEN_BADGE_TEXT,
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '16px',
              }}
            >
              Hidden
            </Box>
          ) : null}
        </Stack>
        <Typography
          sx={{
            fontSize: '16px',
            lineHeight: '24px',
            color: isHiddenPlatformCard ? HIDDEN_BODY : BODY,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {template.shortDescription}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 1, flexWrap: 'wrap' }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              component="img"
              src={metaQuestionsIcon}
              alt=""
              sx={{
                width: '11.667px',
                height: '11.667px',
                display: 'block',
                opacity: isHiddenPlatformCard ? 0.4 : 1,
              }}
            />
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: isHiddenPlatformCard ? HIDDEN_META_QUESTIONS : META_QUESTIONS,
              }}
            >
              {template.questionCount} questions
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              component="img"
              src={metaDurationIcon}
              alt=""
              sx={{
                width: '11.667px',
                height: '11.667px',
                display: 'block',
                opacity: isHiddenPlatformCard ? 0.36 : 1,
              }}
            />
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                color: isHiddenPlatformCard ? HIDDEN_META_DURATION : META_DURATION,
              }}
            >
              {formatTemplateEstCompletionLabel(template.estCompletionSecs)}
            </Typography>
          </Stack>
          {isPlatformAdmin ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <MetaUsageIcon muted={isHiddenPlatformCard} />
              <Typography
                data-testid={`platform-template-usage-${template.slug}`}
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: isHiddenPlatformCard ? HIDDEN_META_USAGE : META_USAGE,
                }}
              >
                {formatPlatformUsageLabel(usageCount)}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </>
  )

  if (interactive && onOpen) {
    return (
      <Box
        component="button"
        type="button"
        data-testid={`instrument-template-tile-${template.slug}`}
        data-hidden={isHiddenPlatformCard ? 'true' : undefined}
        onClick={onOpen}
        sx={tileSx}
      >
        {content}
      </Box>
    )
  }

  return (
    <Box
      data-testid={`instrument-template-tile-${template.slug}`}
      data-hidden={isHiddenPlatformCard ? 'true' : undefined}
      sx={tileSx}
    >
      {content}
    </Box>
  )
}
